import { useState } from 'react'
import Button from '../../../../components/common/Button'

/**
 * Step 3: Parsing Options (FEATURE-010)
 * User configures parsing options like vehicle length for queue conversion.
 * Most users will accept defaults and click through quickly.
 *
 * @param {Function} onSubmit - Callback to submit user input
 * @param {boolean} loading - Loading state for submit button
 */
function ParsingOptions({ onSubmit, loading = false }) {
  // Default vehicle length is 25 ft (standard vehicle length)
  const [vehicleLength, setVehicleLength] = useState(25)

  const handleSubmit = () => {
    onSubmit({
      parsing_options: {
        vehicle_length_ft: vehicleLength,
      },
    })
  }

  const handleVehicleLengthChange = (e) => {
    const value = e.target.value
    // Allow empty string for clearing, or parse as float
    if (value === '') {
      setVehicleLength('')
    } else {
      const parsed = parseFloat(value)
      if (!isNaN(parsed)) {
        setVehicleLength(parsed)
      }
    }
  }

  // Validate: must be a number between 1 and 100
  const isValid = vehicleLength !== '' && vehicleLength >= 1 && vehicleLength <= 100
  const isDefault = vehicleLength === 25

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Parsing Options</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure how queue data is calculated. Default values work for most projects.
        </p>
      </div>

      <div className="space-y-6">
        {/* Vehicle Length Input */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <label htmlFor="vehicle-length" className="block text-sm font-medium text-gray-700 mb-1">
            Vehicle Length for Queue Conversion
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Used to convert queue length from vehicles/lane to feet. The formula is:{' '}
            <code className="bg-gray-200 px-1 rounded">
              queue (ft) = queue (veh/ln) × vehicle length
            </code>
          </p>

          <div className="flex items-center gap-3">
            <input
              id="vehicle-length"
              type="number"
              value={vehicleLength}
              onChange={handleVehicleLengthChange}
              className="w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              min="1"
              max="100"
              step="0.5"
            />
            <span className="text-sm text-gray-600">feet</span>

            {isDefault && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Default</span>
            )}
            {!isDefault && isValid && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                Custom value
              </span>
            )}
          </div>

          {!isValid && (
            <p className="text-xs text-red-600 mt-2">
              Please enter a value between 1 and 100 feet.
            </p>
          )}

          <p className="text-xs text-gray-400 mt-3">
            Common values: 20 ft (compact), 25 ft (standard), 27.5 ft (mixed fleet)
          </p>
        </div>

        {/* Info box about when custom values are needed */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <h3 className="text-sm font-medium text-blue-800 mb-1">When to customize</h3>
          <p className="text-xs text-blue-700">
            Most traffic studies use 25 ft. You may want to adjust this if your jurisdiction uses a
            different standard, or if analyzing areas with predominantly larger or smaller vehicles.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
        <Button variant="primary" onClick={handleSubmit} disabled={loading || !isValid}>
          {loading ? 'Processing...' : 'Continue'}
        </Button>
      </div>
    </div>
  )
}

export default ParsingOptions
